import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import PersonnelToast from '../../components/PersonnelToast';
import Panel from 'src/components/common/Panel';
import EmployeeInsuranceDashboard from 'src/models/personnel/EmloyeeInsuranceDashboard';
import { useForm } from 'react-hook-form';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import useFetch from 'use-http';
import EmployeeInsuranceFilteredDashboard from 'src/models/personnel/EmployeeInsuranceFilteredDashboard';
import EmployeeDashboardFilteredObject from 'src/models/personnel/EmployeeDashboardFilteredObject';
import { FaSortAlphaDown, FaSortAlphaUp, FaFileExcel } from 'react-icons/fa';

// (label, model field, column width) - drives both the header cells and their sort buttons,
// matching the sortable-column pattern in DepositReconciliationPage.tsx's Outstanding/Reconciled
// panels.
const INSURANCE_COLUMNS: Array<{ label: string; field: keyof EmployeeInsuranceFilteredDashboard; width: string }> = [
    { label: 'No.', field: 'tsdNo', width: '5%' },
    { label: 'Name', field: 'akaName', width: '18%' },
    { label: 'Annual Salary', field: 'annualPay', width: '5%' },
    { label: 'Health Total', field: 'health', width: '4%' },
    { label: 'Health Company Total', field: 'companyHealth', width: '4%' },
    { label: 'Health Employee Total', field: 'employeeHealth', width: '4%' },
    { label: 'Vision Total', field: 'vision', width: '4%' },
    { label: 'Vision Company Total', field: 'companyVision', width: '4%' },
    { label: 'Vision Employee Total', field: 'employeeVision', width: '4%' },
    { label: 'Dental Total', field: 'dental', width: '4%' },
    { label: 'Dental Company Total', field: 'companyDental', width: '4%' },
    { label: 'Dental Employee Total', field: 'employeeDental', width: '4%' },
    { label: 'Life Total', field: 'life', width: '4%' },
    { label: 'Life Company Total', field: 'companyLife', width: '4%' },
    { label: 'Life Employee Total', field: 'employeeLife', width: '4%' },
    { label: 'Disability Total', field: 'disability', width: '4%' },
    { label: 'Disability Company Total', field: 'companyDisability', width: '4%' },
    { label: 'Disability Employee Total', field: 'employeeDisability', width: '4%' },
    { label: 'Total', field: 'total', width: '4%' },
    { label: 'Company Total', field: 'companyTotal', width: '4%' },
    { label: 'Employee Total', field: 'employeeTotal', width: '4%' },
];

interface IInsuranceDashboardDialogProps {
    dialogName: string;
    hideDialog: () => void;
    showToast: (message: string, color?: string, persistent?: boolean) => void;
}

const InsuranceDashboardDialog = (props: IInsuranceDashboardDialogProps) => {
    const dispatch = useDispatch();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);
    const [baseDashboardInformation, setBaseDashboardInformation] = useState<Array<EmployeeInsuranceDashboard>>();
    const [filteredDashboardInformation, setFilteredDashboardInformation] = useState<Array<EmployeeInsuranceFilteredDashboard>>();
    const { register, setValue, getValues, reset } = useForm();
    const [benefitYears, setBenefitYears] = useState<Array<number>>();
    const { get, put, response } = useFetch();
    const [filterOptions, setFilterOptions] = useState<EmployeeDashboardFilteredObject>({ timeSpan: 'Annual', year: undefined, activeFilter: 'Active' });
    const [sortField, setSortField] = useState<keyof EmployeeInsuranceFilteredDashboard | undefined>(undefined);
    const [sortDescending, setSortDescending] = useState<boolean>(false);

    useEffect(() => {
        onCreate();
    }, []);

    useEffect(() => {
        getFilteredResults();
    }, [baseDashboardInformation]);

    useEffect(() => {
        if (filterOptions.year !== undefined) {
            getDashboardInformation();
        }
    }, [filterOptions]);

    const onCreate = () => {
        initializeData();
    };

    const initializeData = async () => {
        setValue('filterBy', 'Annual');
        setValue('view', 'Active');
        await getBenefitYears();
    };

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };
    
    const getBenefitYears = async () => {
        let results = await get(`/v1.0/landmen/benefits/get-benefit-years`);

        if (response.ok && results) {
            setBenefitYears(results);
            reset({
                year: results[0],
                filterBy: 'Annual',
                view: 'Active'
            });
            setFilterOptions(prev => ({ ...prev, year: results[0] }));
        } else {
            showToast('Unable to fetch benefit years', `rgba(255,0,0,0.5)`);
        }
    };

    const benefitYearsDropdown = () => {
        return benefitYears
            ? benefitYears?.map((ct: number) => {
                return { value: ct, label: ct.toString() };
            })
            : [];
    };

    const getDashboardInformation = async () => {
        let results = await put(`v1.0/landmen/benefits/employee-insurance-dashboard`, filterOptions);

        if (response.ok) {
            setBaseDashboardInformation(results);
        } else {
            showToast('Unable to fetch insurance dashboard information: ' + response, `rgba(255,0,0,0.5)`);
        }
    };

    // filter
    const getFilteredResults = () => {
        let filteredResults = new Array<EmployeeInsuranceFilteredDashboard>();
        let filter = filterOptions.timeSpan;

        switch (filter) {
            case 'Annual':
                baseDashboardInformation?.forEach((entry) => {
                    let newRow = new EmployeeInsuranceFilteredDashboard();
                    newRow.tsdNo = entry?.tsdNo;
                    newRow.akaName = entry?.akaName;
                    newRow.annualPay = entry?.annualPay;
                    newRow.healthCode = entry?.healthCode;
                    newRow.defined = entry?.defined;
                    newRow.active = entry?.active;

                    newRow.health = entry?.annualHealth;
                    newRow.companyHealth = entry?.companyAnnualHealth;
                    newRow.employeeHealth = entry?.employeeAnnualHealth;

                    newRow.disability = entry?.annualDisability;
                    newRow.companyDisability = entry?.companyAnnualDisability;
                    newRow.employeeDisability = entry?.employeeAnnualDisability;

                    newRow.dental = entry?.annualDental;
                    newRow.companyDental = entry?.companyAnnualDental;
                    newRow.employeeDental = entry?.employeeAnnualDental;

                    newRow.life = entry?.annualLife;
                    newRow.companyLife = entry?.companyAnnualLife;
                    newRow.employeeLife = entry?.employeeAnnualLife;

                    newRow.vision = entry?.annualVision;
                    newRow.companyVision = entry?.companyAnnualVision;
                    newRow.employeeVision = entry?.employeeAnnualVision;

                    newRow.total = entry?.annualTotal;
                    newRow.companyTotal = entry?.companyAnnualTotal;
                    newRow.employeeTotal = entry?.employeeAnnualTotal;

                    filteredResults.push(newRow);
                });
                break;
            case 'Month':
                baseDashboardInformation?.forEach((entry) => {
                    let newMonthRow = new EmployeeInsuranceFilteredDashboard();
                    newMonthRow.tsdNo = entry?.tsdNo;
                    newMonthRow.akaName = entry?.akaName;
                    newMonthRow.annualPay = entry?.annualPay;
                    newMonthRow.healthCode = entry?.healthCode;
                    newMonthRow.defined = entry?.defined;
                    newMonthRow.active = entry?.active;

                    newMonthRow.health = entry?.monthHealth;
                    newMonthRow.companyHealth = entry?.companyMonthHealth;
                    newMonthRow.employeeHealth = entry?.employeeMonthHealth;

                    newMonthRow.disability = entry?.monthDisability;
                    newMonthRow.companyDisability = entry?.companyMonthDisability;
                    newMonthRow.employeeDisability = entry?.employeeMonthDisability;

                    newMonthRow.dental = entry?.monthDental;
                    newMonthRow.companyDental = entry?.companyMonthDental;
                    newMonthRow.employeeDental = entry?.employeeMonthDental;

                    newMonthRow.life = entry?.monthLife;
                    newMonthRow.companyLife = entry?.companyMonthLife;
                    newMonthRow.employeeLife = entry?.employeeMonthLife;

                    newMonthRow.vision = entry?.monthVision;
                    newMonthRow.companyVision = entry?.companyMonthVision;
                    newMonthRow.employeeVision = entry?.employeeMonthVision;

                    newMonthRow.total = entry?.monthTotal;
                    newMonthRow.companyTotal = entry?.companyMonthTotal;
                    newMonthRow.employeeTotal = entry?.employeeMonthTotal;

                    filteredResults.push(newMonthRow);
                });
                break;
            case 'Period':
                baseDashboardInformation?.forEach((entry) => {
                    let newPeriodRow = new EmployeeInsuranceFilteredDashboard();
                    newPeriodRow.tsdNo = entry?.tsdNo;
                    newPeriodRow.akaName = entry?.akaName;
                    newPeriodRow.annualPay = entry?.annualPay;
                    newPeriodRow.healthCode = entry?.healthCode;
                    newPeriodRow.defined = entry?.defined;
                    newPeriodRow.active = entry?.active;

                    newPeriodRow.health = entry?.periodHealth;
                    newPeriodRow.companyHealth = entry?.companyPeriodHealth;
                    newPeriodRow.employeeHealth = entry?.employeePeriodHealth;

                    newPeriodRow.disability = entry?.periodDisability;
                    newPeriodRow.companyDisability = entry?.companyPeriodDisability;
                    newPeriodRow.employeeDisability = entry?.employeePeriodDisability;

                    newPeriodRow.dental = entry?.periodDental;
                    newPeriodRow.companyDental = entry?.companyPeriodDental;
                    newPeriodRow.employeeDental = entry?.employeePeriodDental;

                    newPeriodRow.life = entry?.periodLife;
                    newPeriodRow.companyLife = entry?.companyPeriodLife;
                    newPeriodRow.employeeLife = entry?.employeePeriodLife;

                    newPeriodRow.vision = entry?.periodVision;
                    newPeriodRow.companyVision = entry?.companyPeriodVision;
                    newPeriodRow.employeeVision = entry?.employeePeriodVision;

                    newPeriodRow.total = entry?.periodTotal;
                    newPeriodRow.companyTotal = entry?.companyPeriodTotal;
                    newPeriodRow.employeeTotal = entry?.employeePeriodTotal;

                    filteredResults.push(newPeriodRow);
                });
                break;
            case 'Day':
                baseDashboardInformation?.forEach((entry) => {
                    let newDayRow = new EmployeeInsuranceFilteredDashboard();
                    newDayRow.tsdNo = entry?.tsdNo;
                    newDayRow.akaName = entry?.akaName;
                    newDayRow.annualPay = entry?.annualPay;
                    newDayRow.healthCode = entry?.healthCode;
                    newDayRow.defined = entry?.defined;
                    newDayRow.active = entry?.active;

                    newDayRow.health = entry?.dayHealth;
                    newDayRow.companyHealth = entry?.companyDayHealth;
                    newDayRow.employeeHealth = entry?.employeeDayHealth;

                    newDayRow.disability = entry?.dayDisability;
                    newDayRow.companyDisability = entry?.companyDayDisability;
                    newDayRow.employeeDisability = entry?.employeeDayDisability;

                    newDayRow.dental = entry?.dayDental;
                    newDayRow.companyDental = entry?.companyDayDental;
                    newDayRow.employeeDental = entry?.employeeDayDental;

                    newDayRow.life = entry?.dayLife;
                    newDayRow.companyLife = entry?.companyDayLife;
                    newDayRow.employeeLife = entry?.employeeDayLife;

                    newDayRow.vision = entry?.dayVision;
                    newDayRow.companyVision = entry?.companyDayVision;
                    newDayRow.employeeVision = entry?.employeeDayVision;

                    newDayRow.total = entry?.dayTotal;
                    newDayRow.companyTotal = entry?.companyDayTotal;
                    newDayRow.employeeTotal = entry?.employeeDayTotal;

                    filteredResults.push(newDayRow);
                });
                break;
            default:
                filteredResults = null;
                break;
        }

        setFilteredDashboardInformation(filteredResults);
    };

    const updateFilters = () => {
        let filters = getValues();
        setFilterOptions({ timeSpan: filters.filterBy, year: parseInt(filters.year, 10), activeFilter: filters.view });
    };

    // client-side sort - all rows for the selected year/period are already loaded, so there's no
    // need for a server round-trip the way DepositReconciliationPage.tsx's OData-backed sort needs.
    const handleSort = (field: keyof EmployeeInsuranceFilteredDashboard) => {
        const descending = field === sortField ? !sortDescending : false;
        setSortField(field);
        setSortDescending(descending);

        const sorted = [...(filteredDashboardInformation ?? [])].sort((a, b) => {
            const aVal = a[field];
            const bVal = b[field];
            if (aVal === bVal) { return 0; }
            if (aVal === undefined || aVal === null) { return 1; }
            if (bVal === undefined || bVal === null) { return -1; }
            if (aVal < bVal) { return descending ? 1 : -1; }
            return descending ? -1 : 1;
        });
        setFilteredDashboardInformation(sorted);
    };

    // downloads the real Oracle-equivalent export (XL_Insurance_Dashboard) - one workbook,
    // 4 tabs (Annual/Monthly/Period/Daily), every employee for the year regardless of the
    // Active filter above, matching that source's own behavior exactly.
    const exportToExcel = async () => {
        const year = getValues().year;

        if (!year) {
            showToast('Please select a year first', `rgba(255,0,0,0.5)`);
            return;
        }

        await get(`/v1.0/landmen/benefits/${year}/get-insurance-dashboard-report`);

        if (response.ok) {
            await response.blob().then((blob) => {
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = `InsuranceDashboard_${year}.xlsx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(blobUrl);
            });
        } else {
            showToast('Unable to export report: ' + response, `rgba(255,0,0,0.5)`);
        }
    };

    // Mirrors Oracle's WHEN-MOUSE-DOUBLECLICK [oid 1470]: jumps to that employee/year on the main
    // Benefits tab (Populate_LM). Same localStorage handoff YearMatrixDialog.tsx already uses -
    // drop the target off and let PersonnelSearch.tsx/EmployeeBenefitsPage.tsx pick it up on their
    // own next mount rather than dispatching/fetching from this about-to-unmount dialog.
    const drillIntoEmployee = (tsdNo: number, year: number) => {
        localStorage.setItem('personnelPendingTsdNo', tsdNo.toString());
        localStorage.setItem('personnelPendingYear', year.toString());
        props.hideDialog();
        dispatch(push('/personnel/employee-benefits'));
    };

    return (
        <Dialog title="Insurance Dashboard" dialogName={props.dialogName} bodyStyle={{ width: '85%', height: '75%' }}>
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <Panel backgroundColor="#D3D3D3">
                <Panel>
                    <form>
                        <div className="form-row">

                            <RHFSelect
                                name="year"
                                label="Year"
                                labelOnLeft={true}
                                options={benefitYearsDropdown()}
                                register={register}
                                onChange={() => updateFilters()}
                            />
                            <RHFSelect
                                name="filterBy"
                                label="Insurance By"
                                labelOnLeft={true}
                                options={[
                                    { value: 'Annual', label: 'Annual' },
                                    { value: 'Month', label: 'Month' },
                                    { value: 'Period', label: 'Pay Period' },
                                    { value: 'Day', label: 'Day' },
                                ]}
                                register={register}
                                onChange={() => updateFilters()}
                            />
                            <RHFSelect
                                name="view"
                                label="View"
                                labelOnLeft={true}
                                options={[
                                    { value: 'Active', label: 'Active' },
                                    { value: 'Inactive', label: 'Inactive' },
                                    { value: 'All', label: 'All' },
                                ]}
                                register={register}
                                onChange={() => updateFilters()}
                            />
                        </div>
                    </form>
                    <div style={{ overflowY: 'scroll', maxHeight: '50vh' }}>
                        <table id="insurance-table" style={{ width: '100%' }}>
                            <thead>
                                <tr style={{ background: '#d6d6d6' }}>
                                    {INSURANCE_COLUMNS.map(({ label, field, width }) => {
                                        const willSortDescending = field === sortField ? !sortDescending : false;
                                        const SortIcon = willSortDescending ? FaSortAlphaDown : FaSortAlphaUp;
                                        return (
                                            <th
                                                key={field}
                                                style={{ width, cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                className="sticky-header"
                                                title={`Sort ${label} ${willSortDescending ? 'Descending' : 'Ascending'}`}
                                                onClick={() => handleSort(field)}
                                            >
                                                {label}
                                                <SortIcon style={{ width: '0.9rem', height: '0.9rem', verticalAlign: 'middle', marginLeft: '.2rem' }} />
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            {/* {isLoading && (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: '50vh',
                                        width: '100%',
                                    }}
                                >
                                    <LoadingSpinner />
                                </div>
                            )} */}
                            {filteredDashboardInformation?.map((row: EmployeeInsuranceFilteredDashboard, index: number) => {
                                return (
                                    <tbody key={index}>
                                        <tr
                                            style={{ backgroundColor: row.defined === 'N' ? `rgb(255, 114, 118)` : undefined, cursor: 'pointer' }}
                                            title={`Double-click to open ${row.akaName}'s ${filterOptions.year} Benefits page`}
                                            onDoubleClick={() => drillIntoEmployee(row.tsdNo, filterOptions.year)}
                                        >
                                            <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {row.tsdNo}
                                            </td>
                                            <td style={{ width: '18%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {row.akaName}
                                            </td>
                                            <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.annualPay)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.health)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.companyHealth)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.employeeHealth)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.vision)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.companyVision)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.employeeVision)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.dental)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.companyDental)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.employeeDental)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.life)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.companyLife)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.employeeLife)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.disability)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.companyDisability)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.employeeDisability)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.total)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.companyTotal)}
                                            </td>
                                            <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(row?.employeeTotal)}
                                            </td>
                                        </tr>
                                    </tbody>
                                );
                            })}
                        </table>
                    </div>
                    <div className="button-row" style={{ paddingBottom: '1rem' }}>
                        <Button
                            text="Export to Excel"
                            style={{ width: '25%' }}
                            reactIcon={FaFileExcel}
                            onClick={() => exportToExcel()}
                            intent="secondary"
                        />
                        <Button text="Close" style={{ width: '25%' }} onClick={() => props.hideDialog()} intent="primary" />
                    </div>
                </Panel>
            </Panel>
        </Dialog>
    );
};

export default InsuranceDashboardDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/benefits/InsuranceDashboardDialog.tsx